import "./SubscriptionUpgrade.scss";

import {
  Alert,
  Button,
  Divider,
  ExclamationTriangleIcon,
  Flex,
  Segment,
  Text,
} from "@fluentui/react-northstar";
import FormState, { FormData } from "@shopify/react-form-state";
import { StandardPlanLevels } from "@teams/types";
import {
  StripeBillingInfoDTO,
  StripeSubscriptionChangeDTO,
  UserSubscriptionDTO,
} from "@teams/types/api";
import moment from "moment";
import React, { useCallback, useEffect, useMemo, useState } from "react";

import { useUserInfo } from "../../actions/useUserInfo";
import { isError } from "../../services/ChannelApi";
import { paymentsApi } from "../../services/PaymentsApi";
import { useTeamsContext } from "../../state/TeamsContextProvider";
import { getPlanLabel } from "../../types/subscriptionHelper";
import { getPlanLevelDisplayText, getPlanType } from "../../utils/paymentHelpers";
import { getPageContext } from "../../utils/teamsHelpers";
import { TeamsLoader } from "../TeamsLoader";
import { UserLicensesSelectionField } from "./components/UserLicensesSelectionField";
import { LicensedPricingTable } from "./Pricing/LicensedPricingTable";
import { PlanLevelInfo } from "./StripeAgileCheckoutPage";
import { PollyIcon } from "../PollyIcon";

export const SubscriptionUpgrade: React.FC<{ onUpgraded: () => void }> = ({ onUpgraded }) => {
  const { user, loadUser } = useUserInfo();
  const { teamsContext } = useTeamsContext();
  const [isPollySubProvider, setIsPollySubProvider] = useState<boolean>();
  const [loadError, setLoadError] = useState<Error>();
  const [subscriptionInfo, setSubscriptionInfo] = useState<StripeBillingInfoDTO>();
  const [upgradeError, setUpgradeError] = useState<Error>();
  const [planLevels, setPlanLevels] = useState<StandardPlanLevels[]>();
  const [currentPlan, setCurrentPlan] = useState<StandardPlanLevels>();
  const [selectedPlan, setSelectedPlan] = useState<StandardPlanLevels>();
  const [licenseCount, setLicenseCount] = useState(1);
  const [changeEstimationData, setChangeEstimationData] = useState<StripeSubscriptionChangeDTO>();
  const [loadingChangeEstimate, setLoadingChangeEstimate] = useState(false);
  const [onLegacyPlan, setOnLegacyPlan] = useState<boolean>();

  const payedSub = useMemo(
    () =>
      [...user.subscriptions, ...user.adminSubscriptions].find(
        sub => sub.state === "active" && sub.type === "full"
      ),
    [user]
  );

  const planType = useMemo(() => {
    return getPlanType(selectedPlan?.id);
  }, [selectedPlan?.id]);

  useEffect(() => {
    (async () => {
      const levels = await paymentsApi.getPricing(teamsContext);
      if (!isError(levels)) {
        setPlanLevels(levels);
      }
    })();
  }, [teamsContext]);

  useEffect(() => {
    if (!payedSub?.isOwner && !payedSub?.isAdmin) {
      return;
    }

    (async () => {
      if(payedSub.provider === "polly") {
        setIsPollySubProvider(true)
        return;
      }

      const result = await paymentsApi.getStripeSubscriptionBillingInfo(teamsContext, payedSub.id);
      if (isError(result)) {
        setLoadError(result);
        console.error(result);
        return;
      }

      setSubscriptionInfo(result);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [payedSub?.isOwner, payedSub?.isAdmin]);

  useEffect(() => {
    if (!planLevels || !subscriptionInfo) {
      return;
    }
    // For now we identify the plan with it's planLevel and frequency. A better way to do this would be to use the planId.
    // But because of a historical bug, planId in the subscription object is not upto date for subscriptions that have been updated.
    // We need to write a script that fixes the incorrect planIds, then we can use the planId for this purpose.
    const currentUserPlan = planLevels.find(
      p =>
        p.responseQuantity === subscriptionInfo.planLevel &&
        p.paymentFrequency === subscriptionInfo.paymentFrequency
    );

    let planFound: boolean = false;
    if (currentUserPlan) {
      const lastPaymentAmount = subscriptionInfo.amount / 100;
      const priceForCurrentPlan = getPriceForSelectedPlan(currentUserPlan);
      planFound = priceForCurrentPlan === lastPaymentAmount;
    }

    if (!planFound) {
      setOnLegacyPlan(true);
      setSelectedPlan(undefined);
      setCurrentPlan(undefined);
    } else {
      setSelectedPlan(currentUserPlan);
      setCurrentPlan(currentUserPlan);
    }
  }, [planLevels, subscriptionInfo]);

  useEffect(() => {
    (async () => {
      if (getPageContext() === "/agile" || !payedSub || !selectedPlan) {
        return;
      }

      setUpgradeError(undefined);
      setLoadingChangeEstimate(true);
      const result = await paymentsApi.getSubscriptionUpgradeEstimate(teamsContext, payedSub.id, {
        licenses: planType === "Standard" ? 1 : licenseCount,
        planId: selectedPlan!.id,
        frequency: selectedPlan!.paymentFrequency,
      });
      setLoadingChangeEstimate(false);

      if (isError(result)) {
        console.error(result);
        setUpgradeError(result);
        setChangeEstimationData(undefined);
        return;
      } else {
        setChangeEstimationData(result);
      }
    })();
  }, [licenseCount, payedSub, planType, selectedPlan, teamsContext]);

  if (!payedSub?.isOwner && !payedSub?.isAdmin) {
    return (
      <Alert
        danger
        content="You do not have a active paid subscription to upgrade, or you are not the subscription owner or administrator!"
      />
    );
  }

  if (isPollySubProvider || loadError) {
    return (
      <Alert
        warning
        content={<div style={{padding: "5px"}} >Please contact our support team to make any changes to your account.{" "}
         <PollyIcon
          primary
          ignoreTheme={true}
          icon="announcement-icon"
          label={<a href="https://www.polly.ai/contact" target="__blank" style={{ color: "#0097FE", textDecoration: "none" }}>
            Click here to contact us
          </a>}
        />
       </div>}
      />
    );
  }

  if (!subscriptionInfo) {
    return <TeamsLoader loadingMessage="Loading subscription..." isLoading />;
  }

  const onPlanUpgradeSubmit = async (formDetails: FormData<PlanUpgrade>) => {
    const { fields } = formDetails;

    setUpgradeError(undefined);
    const result = await paymentsApi.upgradeSubscription(teamsContext, payedSub.id, {
      licenses: fields.licenses.value,
      planId: fields.planId!.value!,
      frequency: selectedPlan!.paymentFrequency,
    });

    if (isError(result)) {
      console.error(result);
      setUpgradeError(result);
      return;
    }

    await loadUser(true);
    onUpgraded();
  };

  if (!planLevels || !subscriptionInfo) {
    return <TeamsLoader isLoading loadingMessage="Loading..." />;
  }

  const initialValues: PlanUpgrade = {
    licenses: subscriptionInfo.quantity,
    planId: currentPlan?.id,
  };

  return (
    <Flex column gap="gap.medium" className="subscription-upgrade">
      <Text content="Current Plan" weight="bold" />
      <Flex column gap="gap.small" className="subscription-upgrade__plan-info">
        <Flex gap="gap.small">
          <Text content="Monthly responses:" weight="bold" />
          <Text content={getPlanLevelDisplayText(subscriptionInfo.planLevel)} />
        </Flex>
        <Flex gap="gap.small">
          <Text content="Licenses:" weight="bold" />
          <Text content={subscriptionInfo.quantity} />
        </Flex>
        {onLegacyPlan && (
          <Alert
            warning
            icon={<ExclamationTriangleIcon />}
            content={
              "Your'e currently on a plan that's no longer supported, please contact support or switch to an available plan."
            }
          />
        )}
      </Flex>

      <Text content="Change Plan" weight="bold" />
      <FormState validateOnSubmit initialValues={initialValues} onSubmit={onPlanUpgradeSubmit}>
        {({ fields, submitting, submit }) => {
          const selectedLevel = planLevels.find(p => p.id === fields.planId?.value);
          if (licenseCount !== fields.licenses.value) {
            setLicenseCount(fields.licenses.value);
          }

          const isCurrentPlanSelected =
            currentPlan?.id === fields.planId!.value &&
            subscriptionInfo.quantity === fields.licenses.value;
          return (
            <Flex column gap="gap.large" className="subscription-upgrade__plan-info">
              <Flex column gap="gap.medium">
                <Flex hAlign="center" space="evenly" gap="gap.small" wrap>
                  {planLevels.map(level => (
                    <PlanLevelInfo
                      key={level.id}
                      plan={level}
                      isSelected={fields.planId?.value === level.id}
                      disabled={submitting}
                      onSelected={plan => {
                        fields.planId?.onChange(plan.id);
                        setSelectedPlan(plan);
                        if (getPlanType(plan.id) === "Standard") {
                          fields.licenses.onChange(1);
                          setLicenseCount(1);
                        } else {
                          fields.licenses.onChange(subscriptionInfo.quantity);
                        }
                      }}
                    />
                  ))}
                </Flex>

                {planType === "Pro" && (
                  <Flex column gap="gap.small">
                    <UserLicensesSelectionField
                      field={fields.licenses}
                      max={100}
                      minAllowed={subscriptionInfo.quantityUsed}
                    />

                    {subscriptionInfo.quantity > 1 && (
                      <Alert
                        content={`There are currently ${subscriptionInfo.quantityUsed} licenses assigned.`}
                      />
                    )}
                  </Flex>
                )}

                {getPageContext() === "/msteams" && !isCurrentPlanSelected && selectedPlan && (
                  <ChangePlanEstimate
                    selectedPlan={selectedPlan}
                    licenseCount={licenseCount}
                    changeEstimationData={changeEstimationData}
                    payedSub={payedSub}
                    isLoading={loadingChangeEstimate}
                  />
                )}

                {getPageContext() === "/agile" && selectedLevel && (
                  <Segment color="brand">
                    <LicensedPricingTable
                      taxPercent={
                        subscriptionInfo.taxPercent ? subscriptionInfo.taxPercent * 0.01 : undefined
                      }
                      licenses={fields.licenses.value}
                      price={getPriceForSelectedPlan(selectedLevel)}
                      frequency={selectedLevel.paymentFrequency}
                      coupon={
                        subscriptionInfo.discountPercent
                          ? {
                              id: "",
                              name: "",
                              percentOff: subscriptionInfo.discountPercent,
                              amountOff: 0,
                            }
                          : undefined
                      }
                      planName={getPlanLabel(
                        getPageContext() === "/agile" ? "agile-standup" : "msteams-standard",
                        payedSub?.planLevel,
                        payedSub?.scope
                      )}
                      showCoupon={false}
                    />
                  </Segment>
                )}
              </Flex>
              {upgradeError && <Alert danger content={upgradeError.message} />}
              <div>
                <Button
                  primary
                  content="Update Plan"
                  onClick={submit}
                  loading={submitting}
                  disabled={submitting || isCurrentPlanSelected}
                />
              </div>
            </Flex>
          );
        }}
      </FormState>
    </Flex>
  );
};

type PlanUpgrade = {
  licenses: number;
  planId?: string;
};

const getPriceForSelectedPlan = (selectedPlan: StandardPlanLevels) => {
  switch (selectedPlan.paymentFrequency) {
    case "monthly":
      return selectedPlan.monthlyPrice;
    case "quarterly":
      return selectedPlan.quarterlyPrice;
    case "semi_annually":
      return selectedPlan.semi_annuallyPrice;
    case "yearly":
      return selectedPlan.yearlyPrice;
  }
};

const ChangePlanEstimate: React.FC<{
  selectedPlan: StandardPlanLevels | undefined;
  licenseCount: number;
  changeEstimationData?: StripeSubscriptionChangeDTO;
  payedSub: UserSubscriptionDTO;
  isLoading: boolean;
}> = ({ selectedPlan, licenseCount, changeEstimationData, payedSub, isLoading }) => {
  const convertToPrice = useCallback((amount: number) => {
    if (amount >= 0) {
      return `US$${amount}`;
    } else {
      return `- US$${amount * -1}`;
    }
  }, []);

  return (
    <TeamsLoader isLoading={isLoading} loadingMessage={"Loading Plan Change Estimate"}>
      {!changeEstimationData?.priceOfNewlySelectedPlan && <></>}
      {changeEstimationData && (
        <Segment color="brand">
          <Flex column gap="gap.small">
            <Flex>
              <Flex column>
                <Text
                  weight="bold"
                  content={`Polly ${getPlanLabel(
                    "msteams-standard",
                    selectedPlan?.responseQuantity
                  )} Plan`}
                />
                <Text
                  content={`${licenseCount} x $${getPriceForSelectedPlan(selectedPlan!)}/${
                    selectedPlan!.paymentFrequency
                  }`}
                  size="smaller"
                />
                <Text
                  content={`Charged from today to ${moment(
                    new Date(changeEstimationData.newPlanEndDateTime)
                  ).format("MMM Do YYYY")}`}
                  size="smaller"
                />
              </Flex>
              <Flex.Item push>
                <Text content={convertToPrice(changeEstimationData.priceOfNewlySelectedPlan)} />
              </Flex.Item>
            </Flex>
            <Divider />
            <Flex>
              <Flex>
                <Text
                  color="brand"
                  content={`Unused Current Polly ${getPlanLabel(
                    "msteams-standard",
                    payedSub.planLevel
                  )} Plan`}
                />
              </Flex>
              <Flex.Item push>
                <Text
                  color="brand"
                  content={convertToPrice(changeEstimationData.refundedPriceOfOldPlan)}
                />
              </Flex.Item>
            </Flex>
            <Divider />
            {changeEstimationData.taxPercent > 0 && (
              <>
                <Flex>
                  <Text content={`Total exculding Tax`} />
                  <Flex.Item push>
                    <Text content={convertToPrice(changeEstimationData.subTotalExculdingTax)} />
                  </Flex.Item>
                </Flex>
                <Divider />
                <Flex>
                  <Text content={`Tax (${changeEstimationData.taxPercent}%)`} />
                  <Flex.Item push>
                    <Text content={convertToPrice(changeEstimationData.tax)} />
                  </Flex.Item>
                </Flex>
                <Divider />
              </>
            )}
            <Flex>
              Total
              <Flex.Item push>
                <Text content={convertToPrice(changeEstimationData.subTotal)} />
              </Flex.Item>
            </Flex>

            {changeEstimationData.subTotal < 0 && (
              <>
                <Divider />
                <Flex>
                  <Text color="brand" content="Applied to Balance" />
                  <Flex.Item push>
                    <Text
                      color="brand"
                      content={convertToPrice(changeEstimationData.subTotal * -1)}
                    />
                  </Flex.Item>
                </Flex>
              </>
            )}

            {changeEstimationData.existingBalance > 0 && changeEstimationData.subTotal > 0 && (
              <>
                <Divider />
                <Flex>
                  <Text color="brand" content="Applied from Balance" />
                  <Flex.Item push>
                    <Text
                      color="brand"
                      content={convertToPrice(
                        (changeEstimationData.existingBalance > changeEstimationData.subTotal
                          ? changeEstimationData.subTotal
                          : changeEstimationData.existingBalance) * -1
                      )}
                    />
                  </Flex.Item>
                </Flex>
              </>
            )}

            <Divider />
            <Flex>
              Amount Due
              <Flex.Item push>
                <Text content={convertToPrice(changeEstimationData.amountDue)} />
              </Flex.Item>
            </Flex>
          </Flex>
        </Segment>
      )}
    </TeamsLoader>
  );
};
